import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoginPage } from "@/components/pages/LoginPage";
import { OnboardingPage } from "@/components/pages/OnboardingPage";
import { AppNavbar } from "@/components/layout/AppNavbar";
import { AppRouting } from "@/components/layout/AppRouting";
import { api, setOnAuthExpired } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import type { AuthUser } from "@/types";
import { getStoredToken, setStoredToken, clearStoredToken, isTokenExpired } from "@/types";

export default function App() {
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const saved = localStorage.getItem("rs_theme");
    return saved === "dark" ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("rs_theme", theme);
  }, [theme]);

  useEffect(() => {
    setOnAuthExpired(() => {
      clearStoredToken();
      queryClient.clear();
      setAuthUser(null);
      setAuthChecked(true);
    });
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    if (!token || isTokenExpired(token)) {
      clearStoredToken();
      setAuthUser(null);
      setAuthChecked(true);
      return;
    }
    api.me()
      .then(({ user }) => setAuthUser(user))
      .catch(() => { clearStoredToken(); setAuthUser(null); })
      .finally(() => setAuthChecked(true));
  }, []);

  const handleLogout = () => {
    clearStoredToken();
    setAuthUser(null);
    queryClient.clear();
    navigate("/");
  };

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  if (!authUser) {
    return <LoginPage onLogin={(user, token) => { setStoredToken(token); setAuthUser(user); navigate("/candidates"); }} />;
  }

  if (authUser.role !== "super_admin" && authUser.org_id === null) {
    return (
      <OnboardingPage
        authUser={authUser}
        onComplete={(user, token) => { setStoredToken(token); setAuthUser(user); navigate("/candidates"); }}
      />
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <AppNavbar
        authUser={authUser}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        onLogout={handleLogout}
        theme={theme}
        onToggleTheme={() => setTheme(theme === "light" ? "dark" : "light")}
      />
      <AppRouting
        authUser={authUser}
        sidebarOpen={sidebarOpen}
        onSidebarClose={() => setSidebarOpen(false)}
        onProfileUpdate={(updates) => setAuthUser((prev) => prev ? { ...prev, ...updates } : prev)}
      />
    </div>
  );
}
